import { AppIcon } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import type {
  MatchedInstitution,
  OperatorContextRoot,
  OperatorRow as OperatorFleetRow,
} from '@backrs_ui/grow-ui/src/redux/institutionAdmin';
import { type OperatorContextSelection } from '@backrs_ui/grow-ui/src/redux/operatorContext';

import {
  Box,
  InputAdornment,
  type SxProps,
  TableCell,
  TableRow,
  TableSortLabel,
  TextField,
  type Theme,
  Typography,
} from '@mui/material';

import { DashboardSectionState } from './DashboardSectionState';
import { OPERATOR_HEADERS, OPERATOR_TABLE_ARIA, OperatorHomeRow, OperatorRow } from './OperatorRow';
import { RostersAdminTable } from './RostersAdminTable';

const strings = appStrings.modules.rostersAdmin.operators;

/** What MUI's pagination takes for a count it has not been told: it keeps the next page
 * reachable and stops asserting a total. */
const UNKNOWN_TOTAL = -1;

type OperatorsTabProps = Readonly<{
  rows: readonly OperatorFleetRow[];
  /** Null where the fleet size did not arrive; the control then offers the next page
   * without claiming how many there are. */
  total: number | null;
  dropped: number;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string | null;
  isSearching: boolean;
  draft: string;
  onDraftChange: (value: string) => void;
  sortDirection: 'ASC' | 'DESC';
  onToggleSort: () => void;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
  activeOperatorId: OperatorContextSelection | null;
  home: OperatorContextRoot | null;
  /** The read that names the home did not land. Distinct from a caller who has none. */
  isContextError: boolean;
  onOpenHome: () => void;
  onOpen: (operator: OperatorFleetRow) => void;
  onOpenMatchedInstitution: (operator: OperatorFleetRow, institution: MatchedInstitution) => void;
}>;

export const OperatorsTab = ({
  rows,
  total,
  dropped,
  isLoading,
  isError,
  errorMessage,
  isSearching,
  draft,
  onDraftChange,
  sortDirection,
  onToggleSort,
  page,
  rowsPerPage,
  onPageChange,
  activeOperatorId,
  home,
  isContextError,
  onOpenHome,
  onOpen,
  onOpenMatchedInstitution,
}: OperatorsTabProps) => {
  const isHomeCurrent = home !== null && activeOperatorId === home.id;

  const headers = [
    <TableSortLabel
      key="name"
      id="rosters-admin-operators-sort-name"
      active
      direction={sortDirection === 'ASC' ? 'asc' : 'desc'}
      onClick={onToggleSort}
    >
      {OPERATOR_HEADERS[0]}
    </TableSortLabel>,
    ...OPERATOR_HEADERS.slice(1),
  ];

  return (
    <Box id="rosters-admin-operators" sx={tabSx}>
      <Box sx={headerSx}>
        <TextField
          id="rosters-admin-operators-search"
          placeholder={strings.searchPlaceholder}
          aria-label={strings.searchLabel}
          value={draft}
          onChange={(event) => onDraftChange(event.target.value)}
          size="small"
          sx={searchSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <AppIcon
                  iconName="SearchMd"
                  diameter="20px"
                  color="Colors/Foreground/fg-quaternary"
                />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={actionsSx}>
          {isContextError ? (
            <Typography
              id="rosters-admin-operators-context-error"
              variant="text-sm-regular"
              role="status"
              sx={mutedSx}
            >
              {strings.contextError}
            </Typography>
          ) : null}
        </Box>
      </Box>
      <DashboardSectionState
        id="rosters-admin-operators-state"
        isLoading={isLoading && rows.length === 0}
        isError={isError}
        errorMessage={errorMessage}
        dropped={dropped}
        isEmpty={rows.length === 0 && home === null}
        strings={isSearching ? { ...strings, empty: strings.emptySearch } : strings}
      >
        <RostersAdminTable
          paginationId="rosters-admin-operators-pagination"
          headers={headers}
          ariaLabel={OPERATOR_TABLE_ARIA}
          paging={{
            visible: rows,
            count: total ?? UNKNOWN_TOTAL,
            page,
            rowsPerPage,
            onPageChange,
          }}
        >
          {home !== null ? (
            <OperatorHomeRow home={home} isActive={isHomeCurrent} onOpen={onOpenHome} />
          ) : null}
          {home !== null && rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={OPERATOR_HEADERS.length}>
                <Typography variant="text-sm-regular" role="status" sx={mutedSx}>
                  {isSearching ? strings.emptySearch : strings.empty}
                </Typography>
              </TableCell>
            </TableRow>
          ) : null}
          {rows.map((operator) => (
            <OperatorRow
              key={operator.id}
              operator={operator}
              activeOperatorId={activeOperatorId}
              onOpen={onOpen}
              onOpenMatchedInstitution={onOpenMatchedInstitution}
            />
          ))}
        </RostersAdminTable>
      </DashboardSectionState>
    </Box>
  );
};

const tabSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  gap: backrs.spacing['spacing-xl'],
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: backrs.spacing['spacing-md'],
});

const actionsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: backrs.spacing['spacing-md'],
});

const mutedSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const searchSx: SxProps<Theme> = {
  alignSelf: 'flex-start',
  width: 320,
  maxWidth: '100%',
};
